import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Snackbar,
  Paper,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import arrowRight from '@/assets/icons/arrow-right.svg';
import copyIcon from '@/assets/icons/copy.svg';
import normalIcon from '@/assets/icons/normal.svg';
import CharacterCounter from '@/components/common/CharacterCounter';
import { configService } from '@/services/configService';

interface ImageGenerationModalsProps {
  // 이미지 확대 보기 모달
  showImageDialog: boolean;
  setShowImageDialog: (show: boolean) => void;
  selectedImage: string | null;

  // 번역 모달
  showTranslateDialog?: boolean;
  setShowTranslateDialog?: (show: boolean) => void;
  translateSource?: string;
  setTranslateSource?: (text: string) => void;
  translateResult?: string;
  setTranslateResult?: (text: string) => void;
  onTranslateClick?: () => void;
  onApplyClick?: () => void;
  onCloseTranslateModal?: () => void;
  isTranslateButtonActive?: boolean;
  isApplyButtonActive?: boolean;
  isTranslating?: boolean;
}

const ImageGenerationModals = ({
  showImageDialog,
  setShowImageDialog,
  selectedImage,
  showTranslateDialog = false,
  setShowTranslateDialog,
  translateSource = '',
  setTranslateSource,
  translateResult = '',
  setTranslateResult,
  onTranslateClick,
  onApplyClick,
  onCloseTranslateModal,
  isTranslateButtonActive = false,
  isApplyButtonActive = false,
  isTranslating = false,
}: ImageGenerationModalsProps) => {
  const [sourceIconCopied, setSourceIconCopied] = useState(false);
  const [resultIconCopied, setResultIconCopied] = useState(false);

  const handleCopySource = () => {
    navigator.clipboard.writeText(translateSource);
    setSourceIconCopied(true);
    setTimeout(() => setSourceIconCopied(false), 2000);
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(translateResult);
    setResultIconCopied(true);
    setTimeout(() => setResultIconCopied(false), 2000);
  };

  const sourceChars = (translateSource || '').length;
  const resultChars = (translateResult || '').length;
  const translationLimit = 1024;

  return (
    <>
      {/* 이미지 확대 보기 모달 / Image Zoom Modal */}
      <Dialog
        open={showImageDialog}
        onClose={() => setShowImageDialog(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: '100vh',
            width: '100vw',
            maxHeight: '100vh',
            maxWidth: '100vw',
            margin: 0,
            borderRadius: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: '100vh',
            width: '100vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* 닫기 버튼 */}
          <IconButton
            onClick={() => setShowImageDialog(false)}
            size="large"
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              zIndex: 1,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'white',
              '&:hover': { 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <Close />
          </IconButton>

          {/* 이미지 */}
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Generated image"
              sx={{
                maxHeight: '100vh',
                maxWidth: '100vw',
                objectFit: 'contain',
                cursor: 'pointer',
              }}
              onClick={() => setShowImageDialog(false)}
            />
          )}
        </Box>
      </Dialog>

      {/* 텍스트 번역 모달 / Translate Text Modal */}
      <Dialog
        open={showTranslateDialog}
        onClose={
          onCloseTranslateModal ||
          (() => setShowTranslateDialog && setShowTranslateDialog(false))
        }
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            width: '932px',
            height: '558px',
            maxWidth: '932px',
            maxHeight: '558px',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ py: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              텍스트 번역
            </Typography>
            <IconButton
              size="small"
              onClick={
                onCloseTranslateModal ||
                (() => setShowTranslateDialog && setShowTranslateDialog(false))
              }
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            pt: 1.5,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={0}
            alignItems="stretch"
            sx={{ flex: 1, overflow: 'hidden' }}
          >
            {/* 한국어 입력 */}
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 1,
                border: 'none',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, textAlign: 'center', mb: 2 }}
              >
                한국어
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: '2px solid',
                  borderColor: 'grey.400',
                  borderRadius: '16px',
                  minHeight: '200px',
                  maxHeight: '300px',
                  position: 'relative',
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* 텍스트 입력 영역 */}
                <Box
                  sx={{
                    flex: 1,
                    padding: '20px 24px',
                    paddingBottom: '60px', // 버튼 영역 공간 확보
                    minHeight: '200px',
                    height: 'calc(300px - 60px)', // 고정 높이로 설정
                  }}
                >
                  <textarea
                    value={translateSource}
                    onChange={e =>
                      setTranslateSource && setTranslateSource(e.target.value)
                    }
                    placeholder="한국어 텍스트를 입력하세요"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'inherit',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      backgroundColor: 'transparent',
                      wordWrap: 'break-word',
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(0,0,0,0.2) transparent',
                    }}
                  />
                </Box>

                {/* 하단 버튼 영역 */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 24px',
                    backgroundColor: 'transparent',
                    minHeight: '60px',
                  }}
                >
                  {/* 복사 버튼 */}
                  <Chip
                    icon={
                      <img
                        src={sourceIconCopied ? normalIcon : copyIcon}
                        alt="copy"
                        style={{ height: 20 }}
                      />
                    }
                    label="복사"
                    onClick={handleCopySource}
                    sx={{
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: 'grey.200' },
                      cursor: 'pointer',
                      px: '6px',
                      height: 36,
                      borderRadius: '8px',
                      '.MuiChip-icon': {
                        marginLeft: 0,
                        marginRight: '4px',
                      },
                      '.MuiChip-label': {
                        px: 0,
                      },
                    }}
                  />

                  {/* 문자 카운터 - 한국어는 현재 자수만 표시 */}
                  <CharacterCounter
                    currentCount={sourceChars}
                    isKoreanOnly={true}
                    label="자"
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onTranslateClick}
                  disabled={!isTranslateButtonActive || isTranslating}
                  sx={{
                    bgcolor: isTranslateButtonActive
                      ? theme => theme.palette.primary.lighter
                      : 'grey.200',
                    color: isTranslateButtonActive
                      ? theme => theme.palette.primary.main
                      : 'grey.500',
                    border: 'none !important',
                    borderRadius: '12px',
                    '&:hover': {
                      bgcolor: isTranslateButtonActive
                        ? theme => theme.palette.primary.lighter
                        : 'grey.200',
                      opacity: isTranslateButtonActive ? 0.8 : 1,
                    },
                  }}
                >
                  {isTranslating ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      번역 중...
                    </Box>
                  ) : (
                    '번역하기'
                  )}
                </Button>
              </Box>
            </Paper>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                height: '100%',
                pb: '60px',
              }}
            >
              <img
                src={arrowRight}
                alt="to"
                style={{ width: 24, height: 24 }}
              />
            </Box>

            {/* 영어 결과 */}
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                p: 2,
                borderRadius: 2,
                border: 'none',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, textAlign: 'center', mb: 2 }}
              >
                영어
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  border: '2px solid',
                  borderColor: resultChars > translationLimit ? 'error.main' : 'grey.400',
                  borderRadius: '16px',
                  minHeight: '200px',
                  maxHeight: '300px',
                  position: 'relative',
                  backgroundColor: 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* 텍스트 입력 영역 */}
                <Box
                  sx={{
                    flex: 1,
                    padding: '20px 24px',
                    paddingBottom: '60px', // 버튼 영역 공간 확보
                    minHeight: '200px',
                    height: 'calc(300px - 60px)', // 고정 높이로 설정
                  }}
                >
                  <textarea
                    value={translateResult}
                    onChange={e =>
                      setTranslateResult && setTranslateResult(e.target.value)
                    }
                    placeholder="번역된 결과가 여기에 표시됩니다"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      fontFamily: 'inherit',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      backgroundColor: 'transparent',
                      wordWrap: 'break-word',
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: 'rgba(0,0,0,0.2) transparent',
                    }}
                  />
                </Box>

                {/* 하단 버튼 영역 */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 24px',
                    backgroundColor: 'transparent',
                    minHeight: '60px',
                  }}
                >
                  {/* 복사 버튼 */}
                  <Chip
                    icon={
                      <img
                        src={resultIconCopied ? normalIcon : copyIcon}
                        alt="copy"
                        style={{ height: 20 }}
                      />
                    }
                    label="복사"
                    onClick={handleCopyResult}
                    sx={{
                      bgcolor: 'transparent',
                      '&:hover': { bgcolor: 'grey.200' },
                      cursor: 'pointer',
                      px: '6px',
                      height: 36,
                      borderRadius: '8px',
                      '.MuiChip-icon': {
                        marginLeft: 0,
                        marginRight: '4px',
                      },
                      '.MuiChip-label': {
                        px: 0,
                      },
                    }}
                  />

                  {/* 문자 카운터 */}
                  <CharacterCounter
                    currentCount={resultChars}
                    maxCount={1024}
                    label="자"
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={onApplyClick}
                  disabled={!isApplyButtonActive || resultChars > translationLimit}
                  sx={{
                    bgcolor: isApplyButtonActive && resultChars <= translationLimit
                      ? theme => theme.palette.primary.lighter
                      : 'grey.200',
                    color: isApplyButtonActive && resultChars <= translationLimit
                      ? theme => theme.palette.primary.main
                      : 'grey.500',
                    border: 'none !important',
                    borderRadius: '12px',
                    '&:hover': {
                      bgcolor: isApplyButtonActive && resultChars <= translationLimit
                        ? theme => theme.palette.primary.lighter
                        : 'grey.200',
                      opacity: isApplyButtonActive && resultChars <= translationLimit ? 0.8 : 1,
                    },
                  }}
                >
                  적용하기
                </Button>
              </Box>
            </Paper>
          </Stack>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default ImageGenerationModals;
